import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExportOutlined, PlayCircleOutlined } from '@ant-design/icons';
import Glp1BridgeCompactTapNativeAds from './Glp1BridgeCompactTapNativeAds';
import Glp1BridgeProviderAccessOverlay from './Glp1BridgeProviderAccessOverlay';
import Glp1BridgeResourceNav from './Glp1BridgeResourceNav';
import SlideDeckViewer from './SlideDeckViewer';
import useGlp1BridgeProviderAccess from './useGlp1BridgeProviderAccess';
import { captureOutboundLinkClick } from '../utils/analytics';

export const GLP1_BRIDGE_RECORDING_URL = 'https://us06web.zoom.us/rec/play/kLOXr7Rg1OANerDQrwheDhi5j4s6sJuhxNPqC20dKELOQItyAJz1ANkxElYvpMSdkp7MXQoQNr8UMFh2.jPbIIysoeQouhq7D?startTime=1782219686000';
export const GLP1_BRIDGE_SLIDES_URL = '/glp1-bridge-slides.pdf';
const GLP1_BRIDGE_SLIDE_COUNT = 28;

const chapters = [
  {
    time: '12:17',
    title: 'Medicare GLP-1 Bridge Program Overview',
    detail: 'Bridge dates, $50 copay design, included products, and patient eligibility.',
  },
  {
    time: '27:16',
    title: 'Medicare Bridge Program Updates',
    detail: 'Operational details, covered formulations, routing, and CMS source updates.',
  },
  {
    time: '41:13',
    title: 'Bridge Program Screening Process Overview',
    detail: 'How to use de-identified screening steps for BMI, diagnosis, and coverage route.',
  },
  {
    time: '52:11',
    title: 'CMS Bridge Program Discussion',
    detail: 'Open questions, workflow implications, and next steps for clinicians.',
  },
];

function RecordingLink({ className = '' }) {
  return (
    <a
      href={GLP1_BRIDGE_RECORDING_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() =>
        captureOutboundLinkClick('glp1-bridge-open-zoom-recording', GLP1_BRIDGE_RECORDING_URL, {
          placement: 'glp1-bridge-recording-page',
        })
      }
      className={className}
    >
      <ExportOutlined aria-hidden="true" />
      Open recording in Zoom
    </a>
  );
}

function Glp1BridgeRecordingContent() {
  useEffect(() => {
    let userInteracted = false;
    const resetScroll = () => {
      if (!userInteracted && window.scrollY > 0) {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    };
    const markUserInteracted = () => {
      userInteracted = true;
    };
    const listenerOptions = { passive: true };

    window.addEventListener('wheel', markUserInteracted, listenerOptions);
    window.addEventListener('touchstart', markUserInteracted, listenerOptions);
    window.addEventListener('keydown', markUserInteracted);
    resetScroll();

    const interval = window.setInterval(resetScroll, 100);
    const stopTimer = window.setTimeout(() => window.clearInterval(interval), 6000);

    return () => {
      window.removeEventListener('wheel', markUserInteracted);
      window.removeEventListener('touchstart', markUserInteracted);
      window.removeEventListener('keydown', markUserInteracted);
      window.clearInterval(interval);
      window.clearTimeout(stopTimer);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#f7f8fa]">
      <section className="bg-[#111111] px-6 pb-10 pt-10 text-white md:px-16 md:pb-14">
        <div className="mx-auto max-w-[1180px]">
          <nav className="mb-8 flex items-center gap-2 text-sm text-white/55">
            <Link to="/resources" className="transition-colors hover:text-white">Resources</Link>
            <span>/</span>
            <Link to="/resources/glp1-bridge" className="transition-colors hover:text-white">
              Medicare GLP-1 Bridge
            </Link>
            <span>/</span>
            <span className="text-white">Recording</span>
          </nav>
          <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_240px] xl:items-start">
            <div className="max-w-none">
              <p className="mb-4 text-[13px] font-semibold uppercase tracking-[0.12em] text-emerald-300">
                Recording + slides
              </p>
              <h1 className="text-4xl font-semibold leading-tight tracking-normal text-white md:text-[52px] lg:whitespace-nowrap">
                Medicare GLP-1 Bridge recording
              </h1>
              <p className="mt-5 max-w-none text-[17px] leading-relaxed text-white/68 lg:whitespace-nowrap">
                A guided walkthrough of Bridge eligibility, product routing, pharmacy processing, and MedHug workflow tools.
              </p>
            </div>
            <Glp1BridgeResourceNav
              current="recording"
              variant="dark"
              layout="side"
              className="xl:sticky xl:top-24"
            />
          </div>
        </div>
      </section>

      <main>
        <section className="bg-[#111111] px-4 pb-12 md:px-8">
          <div className="mx-auto max-w-[1180px]">
            <div className="overflow-hidden rounded-lg border border-white/10 bg-black shadow-[0_30px_80px_rgba(0,0,0,0.35)]">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 bg-white/[0.04] px-4 py-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-white">
                  <PlayCircleOutlined aria-hidden="true" className="text-emerald-300" />
                  Zoom recording
                </div>
                <span className="text-xs font-medium text-white/55">
                  Embedded player includes Zoom summary, captions, and smart chapters when available.
                </span>
              </div>
              <iframe
                src={GLP1_BRIDGE_RECORDING_URL}
                title="Medicare GLP-1 Bridge recording"
                className="h-[620px] w-full border-0 bg-black md:h-[700px]"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <p className="max-w-2xl text-[13px] leading-relaxed text-white/55">
                If the embedded Zoom player is blocked by your browser or network, use the direct Zoom link.
              </p>
              <RecordingLink className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-[#111111] transition hover:bg-emerald-50" />
            </div>
          </div>
        </section>

        <section className="px-6 py-8 md:px-16 md:py-9">
          <div className="mx-auto max-w-[1180px]">
            <div className="mb-4 flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
              <h2 className="text-xl font-semibold tracking-normal text-[#1d1d1f]">Webinar chapters</h2>
              <p className="max-w-xl text-[13px] leading-relaxed text-[#6e6e73] md:text-right">
                Key markers for jumping into the most useful parts of the recording.
              </p>
            </div>
            <ol className="grid gap-x-8 gap-y-4 border-y border-[#e5e5ea] py-4 sm:grid-cols-2 lg:grid-cols-4">
              {chapters.map((chapter) => (
                <li key={chapter.time} className="py-1 pr-2">
                  <span className="font-mono text-[13px] font-semibold text-emerald-700">{chapter.time}</span>
                  <div>
                    <h3 className="text-[15px] font-semibold leading-snug text-[#1d1d1f]">{chapter.title}</h3>
                    <p className="mt-2 text-[13px] leading-relaxed text-[#6e6e73]">{chapter.detail}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <SlideDeckViewer
          title="Slide deck"
          description="Review the Medicare GLP-1 Bridge webinar slides alongside the recording, with quick slide navigation and a direct PDF fallback."
          pdfUrl={GLP1_BRIDGE_SLIDES_URL}
          slideImageBasePath="/slide-decks/glp1-bridge"
          thumbnailImageBasePath="/slide-decks/glp1-bridge/thumbs"
          pageCount={GLP1_BRIDGE_SLIDE_COUNT}
          analyticsEventName="glp1-bridge-open-slide-deck"
          analyticsPlacement="glp1-bridge-recording-page"
          className="border-y border-[#e5e5ea] bg-white px-6 py-12 md:px-16"
        />

        <section className="border-t border-[#e5e5ea] bg-white px-6 py-10 md:px-16">
          <div className="mx-auto flex max-w-[1180px] flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-[#1d1d1f]">Need the rule details while watching?</h2>
              <p className="mt-2 text-[15px] leading-relaxed text-[#6e6e73]">
                Keep the clinical guide open for eligibility, products, PA criteria, and pharmacy billing details.
              </p>
            </div>
            <Link
              to="/resources/glp1-bridge/guide"
              className="inline-flex items-center justify-center rounded-full border border-[#1d1d1f] px-5 py-3 text-sm font-semibold text-[#1d1d1f] transition hover:bg-[#1d1d1f] hover:text-white"
            >
              Open clinical guide
            </Link>
          </div>
        </section>

        <Glp1BridgeCompactTapNativeAds placement="glp1_bridge_recording_bottom" />
      </main>
    </div>
  );
}

export default function Glp1BridgeRecordingPage() {
  const {
    accessError,
    accessLoading,
    handleVerified,
    hasAccess,
  } = useGlp1BridgeProviderAccess();
  const recordingContent = <Glp1BridgeRecordingContent />;

  if (hasAccess) {
    return recordingContent;
  }

  return (
    <Glp1BridgeProviderAccessOverlay
      accessError={accessError}
      accessLoading={accessLoading}
      checkingCopy="If this browser already has a valid provider cookie, the recording and slides will unlock automatically."
      onVerified={handleVerified}
      previewClassName="glp1-locked-recording-preview"
      previewTestId="glp1-locked-recording-preview"
    >
      {recordingContent}
    </Glp1BridgeProviderAccessOverlay>
  );
}
